// explanationEngine.js
//
// Takes a recipe (from ruleEngine.js) and produces the human-facing content
// blocks that make a recipe page substantive rather than a bare settings table:
// why-explanations, confidence framing, "if conditions change" branches,
// an FAQ block, a meta description, and JSON-LD structured data.
//
// This file is also pure JS (no DOM), so it runs the same in the browser
// and could be unit tested directly in Node.
//
// A rule this file now follows throughout: branch on structured values
// (`settings.ndStatus`, `settings.whiteBalanceMode`, `settings.isoLow`),
// never on the display strings. Comparing against literals like
// "No ND needed" meant that the moment ruleEngine returned a different
// phrasing — "Not required", "ND recommended, but not natively supported" —
// the prose fell through to the wrong branch and generated sentences that
// began with the label itself.

import { SITE_LAUNCH_DATE } from "../config/site";

function confidenceLabel(confidence) {
  if (confidence >= 0.85) return { label: "High confidence", tone: "high" };
  if (confidence >= 0.6) return { label: "Good starting point", tone: "medium" };
  return { label: "Need more information", tone: "low" };
}

function shutterRationale(settings, scenario) {
  const angle = settings.shutterAngle || 180;
  const formula = angle === 180 ? "1 / (fps × 2)" : `1 / (fps × 360 / ${angle})`;
  return `${settings.fps}fps was chosen because ${scenario.name.toLowerCase()} footage typically prioritizes ${scenario.priority[0]}. Shutter is set to ${settings.shutter} to follow the ${angle}° shutter rule (${formula}), which keeps motion looking natural rather than strobing or overly smeared.`;
}

function ndRationale(settings, lighting, camera) {
  const stops = settings.ndRequiredStops;

  switch (settings.ndStatus) {
    case "not-needed":
      return `${lighting.name} doesn't put enough light on the sensor to overexpose at ${settings.shutter} and ISO ${settings.isoLow || "base"}, so no ND filter is needed here.`;

    case "recommended": {
      const overBy = stops != null ? `About ${stops} stops` : "Some";
      const at = settings.ndAperture ? `f/${settings.ndAperture}` : "this camera's aperture";
      const chosen = settings.ndChosenStops != null ? ` ${settings.ndFilter} is ${settings.ndChosenStops} stops` : "";
      return `${overBy} of light needs to come off the sensor: at ${at}, ${settings.shutter} and ISO ${settings.isoLow || "base"}, ${lighting.name.toLowerCase()} overexposes by roughly that much.${chosen}, which is the closest filter in this camera's set. ND is the right lever here because the alternatives both cost something, raising shutter speed breaks the ${settings.shutterAngle || 180}° rule and changes how motion reads, and ISO is already at its floor.${settings.ndNote ? " " + settings.ndNote : ""}`;
    }

    case "recommended-unsupported":
      return `${lighting.name} is bright enough to overexpose at ${settings.shutter}, so an ND filter is the correct fix, but the ${camera.name} has no native or first-party ND option.${camera.capabilities.ndNote ? " " + camera.capabilities.ndNote : ""} Without one, your only remaining control is a faster shutter, which will make motion look sharper and choppier than the ${settings.shutterAngle || 180}° ideal.`;

    case "recommended-unknown-strength":
      return `An ND filter is called for in ${lighting.name.toLowerCase()}, but we can't calculate an exact strength for this camera, either its aperture isn't fixed, or its filter set isn't confirmed in our database.${settings.ndNote ? " " + settings.ndNote : ""}`;

    default:
      return `Whether this camera accepts ND filters isn't confirmed in our database, so treat the exposure above as the target and add whatever filtration gets you there.`;
  }
}

function whiteBalanceRationale(settings) {
  if (settings.whiteBalanceMode === "Auto") {
    return "This camera doesn't expose manual white balance, so it's left on Auto. Auto WB can drift between shots as the camera re-reads the scene, if that becomes visible in the edit, the fix has to happen in post rather than in-camera.";
  }
  if (settings.colorTemperatureKelvin == null) {
    return `${settings.whiteBalance} There's no single correct Kelvin value here, pick the dominant light source and lock to that, or plan to correct per-shot in post.`;
  }
  return `Lock it at ${settings.colorTemperature} rather than leaving it on Auto, so color doesn't drift between shots taken a few minutes apart. Re-check and re-lock if the dominant light source changes, moving indoors, the sun going behind clouds, or a window's contribution shifting during the shoot.`;
}

function imageStyleRationale(settings, camera) {
  const notConfirmed = /not confirmed/i.test(settings.sharpness || "");
  const notAdjustable = /not adjustable/i.test(settings.sharpness || "");

  if (notConfirmed) {
    return `We haven't confirmed which image-style controls the ${camera.name} exposes, so we're not going to tell you what to set them to.${settings.imageStyleNote ? ` ${settings.imageStyleNote}` : ""} Check the menu, if the controls are there, the principle below still applies.`;
  }
  if (notAdjustable) {
    return `${camera.name} doesn't expose separate in-camera sharpness, contrast, or saturation controls.${settings.imageStyleNote ? ` ${settings.imageStyleNote}` : ""}`;
  }
  return `Sharpness ${settings.sharpness}, contrast ${settings.contrast}, saturation ${settings.saturation}. ${
    settings.requiresGrading
      ? "Minimized because this footage is meant to be graded, in-camera sharpening, contrast, and saturation are baked into the file and can't be removed later, and fight against a color grade."
      : "Left near neutral for accurate, ungraded color straight out of camera."
  }${settings.imageStyleNote ? ` ${settings.imageStyleNote}` : ""}`;
}

function buildWhyBlocks(recipe, { camera, scenario, lighting }) {
  const { settings } = recipe;
  const blocks = [
    {
      title: "Lighting conditions",
      text: lighting.description,
    },
    {
      title: "Resolution & frame rate",
      text: settings.resolutionKnown === false
        ? `${settings.fps}fps is the right rate for this shot.${settings.resolutionNote ? ` ${settings.resolutionNote}` : ""}`
        : `${settings.resolution} at ${settings.fps}fps.${settings.resolutionNote ? ` ${settings.resolutionNote}` : ""} Resolution here is the highest this camera can actually deliver at ${settings.fps}fps, the two are picked together, because most cameras cap resolution as frame rate climbs.`,
    },
    {
      title: "Frame rate & shutter",
      text: shutterRationale(settings, scenario),
    },
    {
      title: "ISO",
      text: settings.isoGuidance
        ? settings.isoGuidance
        : "This camera's ISO range isn't confirmed in our database, so we can't give a precise recommendation, start as low as the camera allows and raise only as needed to get a correct exposure.",
    },
    {
      title: "ND filter",
      text: ndRationale(settings, lighting, camera),
    },
    {
      title: "White balance",
      text: whiteBalanceRationale(settings),
    },
    {
      title: "Picture profile",
      text: settings.requiresGrading
        ? `${settings.profile} is a flat profile, footage will look low-contrast and desaturated straight out of camera and needs color grading in post to reach its final look. This trades a small amount of upfront simplicity for more flexibility when editing.`
        : `${settings.profile} is used here for a look that's usable straight out of camera with little to no color grading required.${settings.profileAlternative ? ` If you're comfortable grading footage, ${settings.profileAlternative} is available on this camera and gives more dynamic range to work with.` : ""}`,
    },
    {
      title: "Sharpness, contrast & saturation",
      text: imageStyleRationale(settings, camera),
    },
  ];

  if (settings.focusNote) {
    blocks.push({ title: "Focus", text: `${settings.focus}. ${settings.focusNote}` });
  }

  if (settings.aspectRatio) {
    blocks.push({
      title: "Output / platform",
      text: `Framed for ${settings.outputName} at ${settings.aspectRatio}. ${settings.outputNote}`,
    });
  }

  return blocks;
}

function buildConditionalBranches(recipe, { camera }) {
  const branches = [];
  const { settings } = recipe;
  const hasND = settings.ndNeeded && settings.ndStatus === "recommended";

  branches.push({
    condition: "If the light gets brighter",
    change: hasND
      ? "Step up to the next-strongest ND in your set rather than raising shutter speed."
      : "You'll likely need to add an ND filter to avoid overexposing at this shutter speed.",
  });

  branches.push({
    condition: "If the light gets dimmer (e.g. moving into shade or indoors)",
    change: hasND
      ? "Remove or step down the ND filter first, before touching ISO or shutter."
      : "Raise ISO gradually before considering a slower shutter, keep the 180° rule intact if possible.",
  });

  branches.push({
    condition: "If the subject starts moving faster",
    change: "Increase frame rate if the camera supports it, and recalculate shutter using the same 180° rule at the new frame rate, don't just increase shutter speed at the old frame rate, or motion will look unnaturally sharp/choppy.",
  });

  if (camera.capabilities.ndSupport === false) {
    branches.push({
      condition: "If you don't have a third-party ND filter for this camera",
      change: "In bright light, your only remaining controls are a faster shutter (breaking the 180° rule, giving sharper/choppier motion) or a smaller aperture if the camera has variable aperture. Neither is a full substitute for ND, expect some quality trade-off.",
    });
  }

  return branches;
}

function buildFAQ(recipe, { camera, scenario, lighting }) {
  const faqs = [
    {
      q: `What if I don't have an ND filter for the ${camera.name}?`,
      a: camera.capabilities.ndSupport
        ? `The ${camera.name} supports ND filters natively or via an official accessory kit. Without one in bright conditions, you'll need to raise shutter speed above the ${recipe.settings.shutterAngle || 180}° ideal, which changes how motion blur looks in the final footage.`
        : `The ${camera.name} has no official ND filter option. ${camera.capabilities.ndNote || "A third-party clip-on or thread-on ND is the usual workaround."}`,
    },
    {
      q: `Can I use these settings for a different lighting condition?`,
      a: `These exact numbers are tuned for ${lighting.name.toLowerCase()}. Moving to noticeably brighter or dimmer light changes the ND/ISO balance, see the "if conditions change" guidance above rather than reusing these settings unchanged.`,
    },
    {
      q: `Why ${recipe.settings.fps}fps and not something else for ${scenario.name.toLowerCase()}?`,
      a: `${scenario.name} footage is prioritized for ${scenario.priority.slice(0, 2).join(" and ")}. ${recipe.settings.fps}fps balances motion smoothness with the camera's supported frame rates and typical delivery formats (most platforms default to 30fps or 24fps playback).`,
    },
  ];

  if (recipe.settings.resolutionKnown !== false) {
    faqs.push({
      q: `Why ${recipe.settings.resolution} and not the camera's maximum?`,
      a: `${recipe.settings.resolution} is the highest resolution the ${camera.name} can record at ${recipe.settings.fps}fps. Most cameras publish a headline resolution that's only available at their lowest frame rates, so pairing the two matters more than chasing the bigger number.`,
    });
  }

  return faqs;
}

function buildJsonLd(recipe, { camera, scenario, lighting }, faq, url, publishAt) {
  // See src/config/site.js for why datePublished falls back to the site's
  // launch date rather than being omitted, and why dateModified never
  // differs from it.
  const dateInfo = {
    datePublished: publishAt || SITE_LAUNCH_DATE,
    dateModified: publishAt || SITE_LAUNCH_DATE,
  };

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "HowTo",
        "name": `${camera.name} camera settings for ${scenario.name} in ${lighting.name}`,
        "description": `Recommended camera settings, shutter, ISO, ND filter, white balance, and picture profile, for shooting ${scenario.name.toLowerCase()} with a ${camera.name} in ${lighting.name.toLowerCase()} conditions.`,
        ...dateInfo,
        "step": [
          {
            "@type": "HowToStep",
            "name": "Resolution & frame rate",
            "text": recipe.settings.resolutionKnown === false
              ? `${recipe.settings.fps}fps`
              : `${recipe.settings.resolution} at ${recipe.settings.fps}fps`,
          },
          { "@type": "HowToStep", "name": "Shutter speed", "text": recipe.settings.shutter },
          { "@type": "HowToStep", "name": "ISO", "text": recipe.settings.iso },
          { "@type": "HowToStep", "name": "White balance", "text": `${recipe.settings.whiteBalance} (${recipe.settings.colorTemperature})` },
          { "@type": "HowToStep", "name": "Picture profile", "text": recipe.settings.profile },
          { "@type": "HowToStep", "name": "Sharpness / contrast / saturation", "text": `${recipe.settings.sharpness} / ${recipe.settings.contrast} / ${recipe.settings.saturation}` },
          { "@type": "HowToStep", "name": "ND filter", "text": recipe.settings.ndFilter },
          ...(recipe.settings.aspectRatio
            ? [{ "@type": "HowToStep", "name": "Aspect ratio / output", "text": `${recipe.settings.outputName}, ${recipe.settings.aspectRatio}` }]
            : []),
        ],
      },
      {
        "@type": "FAQPage",
        "mainEntity": faq.map((item) => ({
          "@type": "Question",
          "name": item.q,
          "acceptedAnswer": { "@type": "Answer", "text": item.a },
        })),
      },
      url
        ? {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": url.origin },
              { "@type": "ListItem", "position": 2, "name": camera.name, "item": `${url.origin}/cameras/${camera.id}/` },
              { "@type": "ListItem", "position": 3, "name": `${scenario.name} in ${lighting.name}`, "item": url.href },
            ],
          }
        : null,
    ].filter(Boolean),
  };
}

// Truncate on a word boundary. The old `.slice(0, 160)` cut mid-word, so
// descriptions ended on fragments like "ND fil" in the SERP.
function truncateMeta(text, limit = 155) {
  if (text.length <= limit) return text;
  const cut = text.slice(0, limit);
  const lastSpace = cut.lastIndexOf(" ");
  return `${cut.slice(0, lastSpace > 0 ? lastSpace : limit).replace(/[,;:.\s]+$/, "")}…`;
}

/**
 * @param {object} recipe - output of generateRecipe()
 * @param {object} ctx - { camera, scenario, lighting }
 * @param {URL} [url] - current page URL, used for canonical/breadcrumb JSON-LD (optional)
 * @param {string} [publishAt] - ISO date this recipe went live, if it was scheduled.
 *   Omitted (or undefined) for recipes live since first deploy — see
 *   src/config/site.js SITE_LAUNCH_DATE for how that case is dated instead.
 */
export function generateExplanation(recipe, ctx, url, publishAt) {
  const conf = confidenceLabel(recipe.confidence);
  const whyBlocks = buildWhyBlocks(recipe, ctx);
  const conditionalBranches = buildConditionalBranches(recipe, ctx);
  const faq = buildFAQ(recipe, ctx);
  const jsonLd = buildJsonLd(recipe, ctx, faq, url, publishAt);

  // Only real numbers go in the meta description. "ISO Not specified by
  // manufacturer" in a SERP snippet advertises a gap in the database.
  const metaParts = [
    recipe.settings.resolutionKnown === false
      ? `${recipe.settings.fps}fps`
      : `${recipe.settings.resolution}/${recipe.settings.fps}fps`,
    recipe.settings.shutter,
    recipe.settings.isoLow ? `ISO ${recipe.settings.isoLow}` : null,
    recipe.settings.ndShort,
  ].filter(Boolean);

  const metaDescription = truncateMeta(
    `${ctx.camera.name} settings for ${ctx.scenario.name.toLowerCase()} in ${ctx.lighting.name.toLowerCase()}: ${metaParts.join(", ")}.`
  );

  return {
    confidenceLabel: conf.label,
    confidenceTone: conf.tone,
    confidenceReasons: recipe.assumptions,
    whyBlocks,
    conditionalBranches,
    faq,
    metaDescription,
    jsonLd,
  };
}
